import React, { ChangeEvent, FormEvent, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "@reach/router";
import { Link, navigate } from "gatsby";

import * as styles from "../styles/module/header.module.scss";
import { ModalContext } from "./Layout";

type HeaderProps = {
  isTransparent: boolean
}

const Header = (props: HeaderProps) => {
  const headerRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [navLowerOpenMap, setNavLowerOpenMap] = useState({});
  const [query, setQuery] = useState("")

  // スクロールを検知して背景切り替え
  useEffect(() => {
    const handleScroll = () => {
      const header = headerRef.current;
      if (!header) return;

      if (window.scrollY > 100) {
        header.classList.add(`${styles.isScrolled}`);
      } else {
        header.classList.remove(`${styles.isScrolled}`);
      }
    };

    // 初回チェック（ページが既にスクロールされている場合に対応）
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // ナビゲーション表示非表示
  function ToggleNav() {
    setIsNavOpen(prev => {
      const next = !prev;
      if (!next) {
        setNavLowerOpenMap({});
      }
      return next;
    });
  }

  // ナビゲーション表示非表示を検知して背景切り替え
  useEffect(() => {
  const header = headerRef.current;
  if (!header) return;

  if (isNavOpen) {
    header.classList.add(`${styles.isNavOpen}`);
  } else {
    header.classList.remove(`${styles.isNavOpen}`);
  }
}, [isNavOpen]);

  function toggleNavLower(key: string, event?: React.MouseEvent) {
    if (window.innerWidth < 980 && event?.preventDefault) {
      event.preventDefault();
    }

    setNavLowerOpenMap(prev => {
      const isCurrentlyOpen = !!prev[key];
      const newMap: { [k: string]: boolean } = {};
      if (!isCurrentlyOpen) {
        newMap[key] = true;
      }
      return newMap;
    });
  }

  // URL変更時にナビを閉じる
  useEffect(() => {
    setIsNavOpen(false);
    setNavLowerOpenMap({});
  }, [location.pathname]);

  // 検索キーワードをセット
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
  }
  // 検索キーワードをクエリパラメータに渡して検索結果ページへ遷移
  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const originalQuery = query;
    const trimmedQuery = query.trim().replace(/\s+/g, ' ');
    if (!originalQuery) {
      navigate('/search/');
      setQuery("");
      return;
    }
    if (!trimmedQuery) {
      setQuery("");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`)
    setQuery("")
  }

  // リンククリック時に表示する確認モーダル
  const contextValue = useContext(ModalContext);
  if (contextValue === null) {
    throw new Error('Providerが設定されていません。')
  }
  // リンククリック時の処理
  const externalLinkClick = (e, url: string) => {
    e.preventDefault();
    // 確認モーダルを表示
    contextValue.openModal(url);
  };

  return <>
    <header ref={headerRef} className={props.isTransparent ? `${styles.header} ${styles.isTransparent}` : styles.header } onClick={e => {
      if (window.innerWidth > 980) {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a');
        if (anchor instanceof HTMLAnchorElement) {
          anchor.blur();
        }
      }
    }}>
      <Link to="/" className={styles.header__logo}>
        <svg xmlns="http://www.w3.org/2000/svg" width="215" height="34" viewBox="0 0 215 34"><path fill="#231815" d="M85.356 17.559h3.923l6.09 11.855h.045V17.559h3.071v15.855h-3.91l-6.112-11.868h-.049v11.868h-3.058zm17.634 15.855v-15.85h3.458c3.431 0 7.532-.119 7.532 4.286 0 1.868-1.26 3.406-3.297 3.687v.036c.869.07 1.38.925 1.691 1.643l2.565 6.198h-3.573l-1.922-4.956c-.462-1.189-.874-1.634-2.224-1.634h-1.025v6.59zm3.205-13.366v4.274h1.052c1.585 0 3.387-.3 3.387-2.207 0-1.908-1.78-2.067-3.387-2.067zm12.016-2.489h3.2v15.855h-3.2zm13.932 0h3.938l6.085 11.833h.049V17.559h3.067v15.855h-3.915l-6.108-11.868h-.04v11.868h-3.076zm26.313 12.705v2.467c-1.132.595-2.397.939-4.053.939-3.883 0-6.156-2.225-6.156-6.062 0-3.388 1.802-6.181 5.477-6.181 4.359 0 5.602 2.978 5.602 7.058h-8.154c.142 1.885 1.474 2.96 3.373 2.96 1.487 0 2.766-.542 3.911-1.181m-7.284-3.859h5.22c-.089-1.475-.781-2.749-2.49-2.749s-2.588 1.172-2.73 2.749m12.024-2.48h-2.272V21.7h2.268v-2.33l3.067-.991V21.7h2.721v2.225h-2.721v5.458c0 .991.271 1.925 1.438 1.925.555 0 1.079-.114 1.389-.321l.103 2.427a9 9 0 0 1-2.264.26c-2.401 0-3.729-1.48-3.729-3.811zm17.36.269c-.573-.256-1.199-.436-2.184-.436-1.993 0-3.36 1.515-3.36 3.74s1.176 3.85 3.436 3.85c.91 0 1.922-.322 2.476-.551l.142 2.551c-.936.264-1.895.383-3.218.383-4.061 0-6.098-2.634-6.098-6.233 0-3.414 2.108-6.128 5.979-6.128 1.3 0 2.237.137 3.124.392l-.293 2.432zm8.678-2.771c3.502 0 6.178 2.339 6.178 6.207 0 3.357-2.264 6.04-6.178 6.04s-6.152-2.679-6.152-6.04c0-3.863 2.676-6.207 6.152-6.207m0 9.881c2.361 0 2.973-2.084 2.973-4.048 0-1.789-.963-3.467-2.973-3.467-2.011 0-2.948 1.722-2.948 3.467 0 1.942.613 4.048 2.948 4.048m8.992-9.604H201v1.639h.045c.932-1.431 2.29-1.912 3.395-1.912 1.594 0 2.726.622 3.369 2.075.697-1.409 2.18-2.075 3.578-2.075 2.818 0 3.613 1.983 3.613 4.595v7.379h-2.969v-6.969c0-1.093 0-2.635-1.709-2.635-1.98 0-2.224 2.414-2.224 3.934v5.674h-2.974v-6.969c0-1.092 0-2.634-1.709-2.634-1.975 0-2.224 2.414-2.224 3.934v5.674h-2.974V21.718z"/><path fill="#005bac" d="M67.934.26c-.24 5.352-.555 11.114-.555 16.863s.315 11.507.555 16.877h-9.126c.235-5.37.555-11.119.555-16.877S59.043 5.608 58.808.26zM46.136 18.097v.004c3.058 4.626 7.283 10.036 11.913 15.903H47.507c-3.569-5.528-8.296-12.074-12.135-16.81 5.157-1.137 9.343-3.269 9.343-7.405 0-3.005-2.139-5.318-7.324-5.318-2.05 0-3.755.146-5.162.141-.146 4.053-.191 8.291-.191 12.511 0 5.749.324 11.507.559 16.877h-7.638C19.215 25.921 12.584 17.894 6.56 11.3c-.084 5.894.054 14.634.711 22.7H0c.249-5.37.559-11.119.559-16.877S.249 5.608 0 .26h8.775c5.278 7.41 11.306 14.789 16.934 21.075.044-5.82-.133-13.736-.728-21.075C29.131.348 35.443 0 40.316 0s9.157.388 11.852 3.057c1.42 1.401 2.139 3.282 2.139 5.564 0 3.956-3.036 7.63-8.171 9.476"/></svg>
      </Link>
      <div className={`${styles.headerMenu} ${isNavOpen ? styles.open : ''}`}>
        <nav className={styles.globalMenu}>
          <ul className={styles.globalMenu__list}>
            <li><Link to="/feature" className={`${styles.switchLowerList} ${navLowerOpenMap['strengths'] ? styles.open : ''}`} onClick={(e) => {
              toggleNavLower('strengths', e);
            }}>NRIネットコムの強み<span className={styles.__icon}></span></Link>
              <ul className={styles.listLower}>
                <li><Link to="/feature"><span>NRIネットコムの強みTOP</span></Link></li>
                <li><Link to="/feature/solution"><span>ソリューション例</span></Link></li>
              </ul>
            </li>
            <li><Link to="/service">サービス</Link></li>
            <li><Link to="/work">事例</Link></li>
            <li><Link to="/event">セミナー／イベント</Link></li>
            <li><Link to="/company" className={`${styles.switchLowerList} ${navLowerOpenMap['company'] ? styles.open : ''}`} onClick={(e) => {
              toggleNavLower('company', e);
            }}>会社情報<span className={styles.__icon}></span></Link>
              <ul className={styles.listLower}>
                <li><Link to="/company"><span>会社情報TOP</span></Link></li>
                <li><Link to="/company/philosophy"><span>私たちの想い</span></Link></li>
                <li><Link to="/company/message"><span>社長メッセージ</span></Link></li>
                <li><Link to="/company/overview"><span>会社概要</span></Link></li>
                <li><Link to="/company/history"><span>沿革</span></Link></li>
                <li><Link to="/company/organization"><span>組織図</span></Link></li>
                <li><Link to="/company/health-management"><span>健康経営への取り組み</span></Link></li>
                <li><Link to="/company/office"><span>アクセス</span></Link></li>
              </ul>
            </li>
          </ul>
        </nav>

        <div className={`${styles.infoMenu}`}>
          <ul className={styles.infoMenu__list}>
            <li><Link to="/news">お知らせ</Link></li>
            <li><Link to="/books">出版物</Link></li>
            <li><a href="https://tech.nri-net.com/" target="_blank" rel="noopener noreferrer" onClick={(e) => externalLinkClick(e, "https://tech.nri-net.com/")}>ブログ</a></li>
            <li><a href="/recruit/" target="_blank" rel="noopener noreferrer">採用情報</a></li>
          </ul>
          <ul className={`${styles.infoMenu__list} ${styles.button}`}>
            <li><Link to="/download" className="btn--secondary btn--small btn--compact"><span>資料ダウンロード</span></Link></li>
            <li><a href="https://dsx-marketing.nri-net.com/contact/form/general/" target="_blank" rel="noopener noreferrer" className="btn--primary btn--small btn--compact link-hubspot"><span>お問い合わせ</span></a></li>
          </ul>
        </div>

        <div className={styles.headerSearch}>
          <form onSubmit={handleSearch}>
            <input type="search" value={query} onChange={handleInputChange} className={`${styles.headerSearch__input}`} placeholder="サイト内検索" />
            <button type="submit" className={styles.headerSearch__button}>検索</button>
          </form>
        </div>
      </div>

      <div className={`${styles.headerMenu__button}`}>
        <button type="button" className={styles.button} onClick={() => { ToggleNav() }}></button>
      </div>
    </header>
  </>;
};

export default Header;
